import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import AnimatedAction from './AnimatedAction';
import Brand from './Brand';
import { useSearchCommand } from '../app/SearchCommandContext';
import {
  BookmarkIcon,
  GalleryThumbnailsIcon,
  HomeIcon,
  MenuIcon,
  SearchIcon,
  WalletIcon,
} from './icons';

function AppHeader() {
  const location = useLocation();
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { searchOpen, openSearch, closeSearch } = useSearchCommand();
  const [overHero, setOverHero] = useState(location.pathname === '/');
  const isHome = location.pathname === '/';

  useEffect(() => {
    setMenuOpen(false);
    closeSearch();

    if (!isHome) {
      setOverHero(false);
      return undefined;
    }

    let frameId;
    let observer;

    function connectToHero() {
      const heroCopy = document.querySelector('#home-hero-copy');

      if (!heroCopy) {
        frameId = window.requestAnimationFrame(connectToHero);
        return;
      }

      setOverHero(true);
      observer = new IntersectionObserver(
        ([entry]) => setOverHero(entry.isIntersecting),
        { rootMargin: '-80px 0px -42% 0px', threshold: 0.05 },
      );
      observer.observe(heroCopy);
    }

    connectToHero();

    return () => {
      window.cancelAnimationFrame(frameId);
      observer?.disconnect();
    };
  }, [closeSearch, isHome, location.pathname]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        closeSearch();
        return;
      }

      const target = event.target;
      const isEditable = target instanceof HTMLElement && (
        target.isContentEditable
        || target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
      );
      const isCommandShortcut = (event.metaKey || event.ctrlKey)
        && event.key.toLowerCase() === 'k';
      const isSlashShortcut = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;

      if (!isEditable && (isCommandShortcut || isSlashShortcut)) {
        event.preventDefault();
        openSearch();
        setMenuOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeSearch, openSearch]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const query = window.matchMedia('(max-width: 900px)');

    function syncNavigation() {
      navRef.current?.toggleAttribute('inert', query.matches && !menuOpen);
    }

    syncNavigation();
    query.addEventListener?.('change', syncNavigation);
    return () => query.removeEventListener?.('change', syncNavigation);
  }, [menuOpen]);

  const headerClassName = [
    'app-header',
    isHome && overHero ? 'is-over-hero' : 'is-solid',
    menuOpen ? 'has-open-menu' : '',
    searchOpen ? 'has-open-search' : '',
  ].filter(Boolean).join(' ');

  return (
      <header className={headerClassName} ref={headerRef}>
        <div className="app-header__inner">
          <Brand />

          <nav
            id="primary-navigation"
            className={`app-nav${menuOpen ? ' is-open' : ''}`}
            ref={navRef}
            aria-label="Primary navigation"
          >
            <AnimatedAction
              as={NavLink}
              exact
              activeClassName="is-active"
              icon={HomeIcon}
              iconSize={18}
              to="/"
            >
              Home
            </AnimatedAction>
            <AnimatedAction
              as={NavLink}
              activeClassName="is-active"
              icon={WalletIcon}
              iconSize={18}
              to="/accounts"
            >
              Accounts
            </AnimatedAction>
            <AnimatedAction
              as={NavLink}
              activeClassName="is-active"
              icon={GalleryThumbnailsIcon}
              iconSize={18}
              to="/nft"
            >
              NFTs
            </AnimatedAction>
            <AnimatedAction
              as={NavLink}
              activeClassName="is-active"
              icon={BookmarkIcon}
              iconSize={18}
              to="/watchlist"
            >
              Watchlist
            </AnimatedAction>
          </nav>

          <div className="app-header__actions">
            <AnimatedAction
              ref={searchTriggerRef}
              className="header-action search-toggle"
              type="button"
              icon={SearchIcon}
              iconSize={18}
              aria-haspopup="dialog"
              aria-expanded={searchOpen}
              aria-controls="global-search-dialog"
              onClick={() => {
                openSearch(searchTriggerRef.current);
                setMenuOpen(false);
              }}
            >
              <span>Search</span>
              <kbd className="search-toggle__shortcut" aria-hidden="true">⌘K</kbd>
            </AnimatedAction>
            <AnimatedAction
              className="header-action nav-toggle"
              type="button"
              icon={MenuIcon}
              iconSize={18}
              aria-expanded={menuOpen}
              aria-controls="primary-navigation"
              onClick={() => {
                const opening = !menuOpen;
                setMenuOpen(opening);
                closeSearch();
                if (opening) {
                  window.setTimeout(() => {
                    navRef.current?.removeAttribute('inert');
                    navRef.current?.querySelector('a')?.focus({ preventScroll: true });
                  }, 0);
                }
              }}
            >
              <span className="nav-toggle__label">Menu</span>
            </AnimatedAction>
          </div>

          <span className="app-header__network" role="img" aria-label="Ethereum Sepolia testnet">
            <i aria-hidden="true" />
            <span aria-hidden="true">Sepolia testnet</span>
          </span>
        </div>
      </header>
  );
}

export default AppHeader;
